import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getCookie } from "../../Utils/cookies";

const ApprovedDataMaharastra = () => {
  const navigate = useNavigate();
  const [servicePersons, setServicePersons] = useState([]);
  const [loadingServicePersons, setLoadingServicePersons] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isPersonDropdownOpen, setIsPersonDropdownOpen] = useState(false);
  const [installationData, setInstallationData] = useState([]);
  const [loadingInstallations, setLoadingInstallations] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedInstallation, setExpandedInstallation] = useState(null);
  const [selectedState, setSelectedState] = useState("");
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const empData = getCookie("empData") ? JSON.parse(getCookie("empData")) : {};

  // Editing states
  const [editingInstallationId, setEditingInstallationId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Photo inclusion flag
  const [includePhoto, setIncludePhoto] = useState(false);

  const states = [
    "Maharashtra",
    "Haryana",
    "Punjab",
    "Rajasthan",
    "Chhattisgarh",
  ];

  // Function to determine panel count based on system type
  const getPanelCountBySystem = (systemName) => {
    if (!systemName) return 0;
    const system = systemName.toLowerCase();
    if (system.includes("5hp dc")) return 9;
    if (system.includes("3hp dc")) return 6;
    if (system.includes("7.5hp dc")) return 13;
    if (system.includes("10hp ac")) return 16;
    return 0;
  };

  const initializePanelNumbers = (installation) => {
    const systemName = installation.systemId?.systemName;
    const panelCount = getPanelCountBySystem(systemName);
    const existingPanels = installation.panelNumbers || [];
    const newPanelNumbers = [];
    for (let i = 0; i < panelCount; i++) {
      newPanelNumbers.push(existingPanels[i] || "");
    }
    return newPanelNumbers;
  };

  useEffect(() => {
    if (selectedState) {
      fetchServicePersons(selectedState);
    }
  }, [selectedState]);

  // Fetch installations whenever dependencies change
  useEffect(() => {
    if (selectedPerson) {
      fetchInstallationData();
    }
  }, [selectedPerson, currentPage, searchTerm, filterStatus, includePhoto]);

  const fetchServicePersons = async (state) => {
    setLoadingServicePersons(true);
    setError(null);
    setServicePersons([]);
    setSelectedPerson(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/state-wise-service-persons?state=${state}`,
      );
      if (response.data.success) {
        setServicePersons(response.data.data);
      } else {
        throw new Error(
          response.data.message || "Failed to fetch service persons",
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingServicePersons(false);
    }
  };

  const fetchInstallationData = async () => {
    if (!selectedPerson) return;
    setLoadingInstallations(true);
    setDataError(null);
    try {
      // Map status filter to installationDone boolean query param
      let installationDoneParam = "";
      if (filterStatus === "completed") installationDoneParam = "true";
      else if (filterStatus === "pending") installationDoneParam = "false";

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/accepted-installation-data`,
        {
          params: {
            empId: selectedPerson._id,
            page: currentPage,
            limit: itemsPerPage,
            s: searchTerm.trim(),
            installationDone: installationDoneParam,
            includePhoto: includePhoto,
          },
        },
      );

      console.log("🔍 API Response:", response.data); // Debug log

      if (response.data.success) {
        const data = response.data.data || [];
        setInstallationData(data);

        // Robust extraction of total count (supports various field names)
        let total =
          response.data.total ??
          response.data.totalCount ??
          response.data.totalRecords ??
          response.data.pagination?.total ??
          response.data.metadata?.total ??
          null;

        // If total is not provided by backend, fallback to length of returned data
        // (only valid when backend returns all records, i.e., no server-side pagination)
        if (total === null) {
          console.warn("⚠️ Backend did not return 'total' field. Using data.length as total.");
          total = data.length;
          // If we fallback to data.length, we assume no server pagination → only one page
          setTotalPagesFromApi(1);
        } else {
          // Calculate totalPages if not provided
          let totalPages =
            response.data.totalPages ??
            response.data.pagination?.totalPages ??
            (total > 0 ? Math.ceil(total / itemsPerPage) : 1);
          setTotalPagesFromApi(totalPages);
        }

        setTotalItems(total);

        // Sync currentPage if API returns it
        if (response.data.currentPage) {
          setCurrentPage(response.data.currentPage);
        } else if (totalPagesFromApi === 1 && currentPage !== 1) {
          // If only one page exists, ensure currentPage is 1
          setCurrentPage(1);
        }
      } else {
        throw new Error(
          response?.data?.message || "Failed to fetch installation data",
        );
      }
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setDataError(err.message);
      // Reset states on error
      setInstallationData([]);
      setTotalItems(0);
      setTotalPagesFromApi(1);
    } finally {
      setLoadingInstallations(false);
    }
  };

  // Reset to page 1 when search, filter, or photo toggle changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleIncludePhotoChange = (e) => {
    setIncludePhoto(e.target.checked);
    setCurrentPage(1);
  };

  const toggleStateDropdown = () => {
    setIsStateDropdownOpen(!isStateDropdownOpen);
  };

  const togglePersonDropdown = () => {
    if (selectedState && !loadingServicePersons) {
      setIsPersonDropdownOpen(!isPersonDropdownOpen);
    }
  };

  const handleSelectState = (state) => {
    setSelectedState(state);
    setIsStateDropdownOpen(false);
    setSelectedPerson(null);
    setInstallationData([]);
    setCurrentPage(1);
    setSearchTerm("");
    setFilterStatus("all");
    setTotalItems(0);
    setTotalPagesFromApi(1);
  };

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setIsPersonDropdownOpen(false);
    setExpandedInstallation(null);
    setCurrentPage(1);
    setSearchTerm("");
    setFilterStatus("all");
    setTotalItems(0);
    setTotalPagesFromApi(1);
  };

  const handleFillForm = (installation) => {
    navigate("/InstallationMaharashtra", {
      state: {
        saralId: installation.farmerDetails?.saralId,
        empId: selectedPerson._id,
        installationData: installation,
        farmerDetails: installation.farmerDetails,
        selectedState: selectedState,
      },
    });
  };

  const toggleExpandInstallation = (id) => {
    setExpandedInstallation(expandedInstallation === id ? null : id);
    if (expandedInstallation === id) {
      setEditingInstallationId(null);
      setEditFormData({});
    }
  };

  const startEditingAll = (installation) => {
    const panelNumbers = initializePanelNumbers(installation);
    setEditingInstallationId(installation._id);
    setEditFormData({
      pumpNumber: installation.pumpNumber || "",
      motorNumber: installation.motorNumber || "",
      controllerNumber: installation.controllerNumber || "",
      rmuNumber: installation.rmuNumber || "",
      panelNumbers: panelNumbers,
    });
  };

  const cancelEditing = () => {
    setEditingInstallationId(null);
    setEditFormData({});
  };

  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePanelNumberChange = (index, value) => {
    setEditFormData((prev) => {
      const newPanelNumbers = [...prev.panelNumbers];
      newPanelNumbers[index] = value;
      return {
        ...prev,
        panelNumbers: newPanelNumbers,
      };
    });
  };

  const saveAllChanges = async (installationId) => {
    setIsSaving(true);
    try {
      const installationToUpdate = installationData.find(
        (inst) => inst._id === installationId,
      );
      if (!installationToUpdate) {
        throw new Error("Installation not found");
      }

      const originalData = {
        pumpNumber: installationToUpdate.pumpNumber || "",
        motorNumber: installationToUpdate.motorNumber || "",
        controllerNumber: installationToUpdate.controllerNumber || "",
        rmuNumber: installationToUpdate.rmuNumber || "",
        panelNumbers: installationToUpdate.panelNumbers || [],
      };

      const cleanedData = {
        pumpNumber: editFormData.pumpNumber?.trim() || "",
        motorNumber: editFormData.motorNumber?.trim() || "",
        controllerNumber: editFormData.controllerNumber?.trim() || "",
        rmuNumber: editFormData.rmuNumber?.trim() || "",
        panelNumbers: (editFormData.panelNumbers || []).filter(
          (p) => p.trim() !== "",
        ),
      };

      const changedFields = {};
      for (const key in cleanedData) {
        if (Array.isArray(cleanedData[key])) {
          if (
            JSON.stringify(cleanedData[key]) !==
            JSON.stringify(originalData[key])
          ) {
            changedFields[key] = cleanedData[key];
          }
        } else if (cleanedData[key] !== originalData[key]) {
          changedFields[key] = cleanedData[key];
        }
      }

      if (Object.keys(changedFields).length === 0) {
        alert("No changes detected.");
        setIsSaving(false);
        setEditingInstallationId(null);
        return;
      }

      const updateData = {
        farmerSaralId: installationToUpdate.farmerDetails?.saralId,
        empName: empData?.name,
        ...changedFields,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/update-farmer-activity-serial-numbers`,
        updateData,
      );

      if (response.data.success) {
        // Update local state optimistically
        setInstallationData((prev) =>
          prev.map((inst) =>
            inst._id === installationId ? { ...inst, ...changedFields } : inst,
          ),
        );
        cancelEditing();
        alert("Changes saved successfully!");
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error(
        "Error updating installation:",
        err?.response || err.message,
      );
      alert(
        "Error updating: " +
          (err?.response?.data?.error || err?.response?.data?.message),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "Invalid Date") return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderEditableField = (installation, field, label, value) => {
    const isEditing = editingInstallationId === installation._id;

    if (isEditing) {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
          <span className="text-xs text-gray-500 sm:w-24">{label}:</span>
          <input
            type="text"
            value={editFormData[field] || ""}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="p-1 border border-gray-300 rounded text-sm flex-1"
            disabled={isSaving}
            placeholder={`Enter ${label}`}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">{label}:</span>
        <span className="text-sm font-medium">{value || "N/A"}</span>
      </div>
    );
  };

  const renderPanelNumbers = (installation) => {
    const isEditing = editingInstallationId === installation._id;
    const systemName = installation.systemId?.systemName;
    const panelCount = getPanelCountBySystem(systemName);

    if (isEditing) {
      return (
        <div className="mb-4">
          <h4 className="font-medium mb-2">
            Panel Numbers ({editFormData.panelNumbers?.length || 0})
            {systemName && (
              <span className="text-xs text-gray-500 ml-2">
                {" "}
                - Based on {systemName}
              </span>
            )}
          </h4>
          <div className="space-y-2">
            {editFormData.panelNumbers?.map((panel, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2"
              >
                <span className="text-xs text-gray-500 sm:w-24">
                  Panel {index + 1}:
                </span>
                <input
                  type="text"
                  value={panel}
                  onChange={(e) =>
                    handlePanelNumberChange(index, e.target.value)
                  }
                  className="p-1 border border-gray-300 rounded text-sm flex-1"
                  disabled={isSaving}
                  placeholder={`Enter panel ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="mb-4">
        <h4 className="font-medium mb-2">
          Panel Numbers ({installation.panelNumbers?.length || 0})
          {systemName && panelCount > 0 && (
            <span className="text-xs text-gray-500 ml-2">
              {" "}
              - Expected: {panelCount} panels for {systemName}
            </span>
          )}
        </h4>
        <div className="flex flex-wrap gap-2">
          {installation.panelNumbers?.length > 0 ? (
            installation.panelNumbers.map((panel, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 rounded text-xs"
              >
                Panel {index + 1}: {panel}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-500">
              N/A - No panel numbers available
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderItemDetail = (label, value) => (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}:</span>
      <span className="text-sm font-medium">{value || "N/A"}</span>
    </div>
  );

  const DropdownArrow = ({ isOpen }) => (
    <span
      className={`ml-2 inline-block w-0 h-0 border-4 border-transparent ${
        isOpen
          ? "border-b-0 border-t-gray-600 border-l-4 border-r-4"
          : "border-t-0 border-b-gray-600 border-l-4 border-r-4"
      }`}
    />
  );

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPagesFromApi)));
  };

  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="mx-auto p-4">
      <div className="text-xl font-bold mb-4">Approved Installation Data</div>

      <div className="flex flex-col md:flex-row gap-4 mb-4">
        {/* State Dropdown */}
        <div className="relative w-full md:w-64">
          <div
            className="flex justify-between items-center w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
            onClick={toggleStateDropdown}
          >
            <span>{selectedState || "Select state"}</span>
            <DropdownArrow isOpen={isStateDropdownOpen} />
          </div>
          {isStateDropdownOpen && (
            <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 max-h-60 overflow-y-auto">
              {states.map((state) => (
                <div
                  key={state}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectState(state)}
                >
                  {state}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Service Person Dropdown */}
        {selectedState && (
          <div className="relative w-full md:w-64">
            <div
              className={`flex justify-between items-center w-full p-2 border border-gray-300 rounded bg-white cursor-pointer ${
                loadingServicePersons ? "opacity-50" : ""
              }`}
              onClick={togglePersonDropdown}
            >
              <span>
                {loadingServicePersons
                  ? "Loading service persons..."
                  : selectedPerson
                    ? selectedPerson.name
                    : "Select service person"}
              </span>
              {!loadingServicePersons && (
                <DropdownArrow isOpen={isPersonDropdownOpen} />
              )}
            </div>
            {isPersonDropdownOpen &&
              selectedState &&
              !loadingServicePersons && (
                <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 max-h-60 overflow-y-auto">
                  {servicePersons.length > 0 ? (
                    servicePersons.map((person) => (
                      <div
                        key={person._id}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleSelectPerson(person)}
                      >
                        {person.name}
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-gray-500">
                      No service persons found
                    </div>
                  )}
                </div>
              )}
          </div>
        )}

        {/* Search, Filter and Photo Toggle */}
        {selectedPerson && (
          <div className="flex flex-col sm:flex-row gap-2 w-full md:flex-1">
            <input
              type="text"
              placeholder="Search by farmer name, village, block, saralId, pump number..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="p-2 border border-gray-300 rounded flex-grow"
            />
            <select
              value={filterStatus}
              onChange={handleFilterChange}
              className="p-2 border border-gray-300 rounded w-full sm:w-auto"
            >
              <option value="all">All Installations</option>
              <option value="completed">Completed Only</option>
              <option value="pending">Pending Only</option>
            </select>
            <label className="flex items-center gap-2 text-sm whitespace-nowrap px-2">
              <input
                type="checkbox"
                checked={includePhoto}
                onChange={handleIncludePhotoChange}
              />
              Include photos
            </label>
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="overflow-auto max-h-[calc(100vh-200px)]">
        {loadingInstallations && (
          <div className="text-center py-4">Loading installations...</div>
        )}
        {dataError && (
          <div className="text-center py-4 text-red-600">
            Error: {dataError}
          </div>
        )}

        {!selectedState ? (
          <div className="text-center text-gray-500 py-8">
            Please select a state to view service persons
          </div>
        ) : selectedPerson ? (
          installationData.length > 0 || totalItems > 0 ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Installations for {selectedPerson.name} (Total: {totalItems})
                </h2>
                {totalPagesFromApi > 1 && (
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPagesFromApi} (showing{" "}
                    {installationData.length} items)
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {installationData.map((installation) => (
                  <div
                    key={installation._id}
                    className={`border border-gray-200 rounded overflow-hidden ${
                      expandedInstallation === installation._id
                        ? "shadow-lg"
                        : ""
                    }`}
                  >
                    <div
                      className="p-4 bg-gray-50 cursor-pointer"
                      onClick={() => toggleExpandInstallation(installation._id)}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <h3 className="text-lg font-semibold flex flex-wrap items-center gap-2">
                          {installation.farmerDetails?.farmerName || "N/A"}
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              installation.installationDone
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {installation.installationDone
                              ? "Completed"
                              : "Pending"}
                          </span>
                          {installation.farmerDetails?.saralId && (
                            <span className="text-sm text-gray-600">
                              SARAL ID: {installation.farmerDetails.saralId}
                            </span>
                          )}
                        </h3>
                        <div className="flex gap-2">
                          {!installation.installationDone && (
                            <button
                              className="px-3 py-1 bg-yellow-400 text-dark rounded text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFillForm(installation);
                              }}
                            >
                              Fill Form
                            </button>
                          )}
                          {editingInstallationId === installation._id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveAllChanges(installation._id);
                                }}
                                disabled={isSaving}
                                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:opacity-50"
                              >
                                {isSaving ? "Updating..." : "Update All"}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelEditing();
                                }}
                                disabled={isSaving}
                                className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              className="px-3 py-1 bg-black text-white rounded text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingAll(installation);
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-4">
                        <span>
                          Sent on: {formatDate(installation.sendingDate)}
                        </span>
                        {installation.approvalDate && (
                          <span>
                            Approved on: {formatDate(installation.approvalDate)}
                          </span>
                        )}
                        {installation.updatedAt && (
                          <span>
                            Last updated: {formatDate(installation.updatedAt)}
                          </span>
                        )}
                      </div>
                    </div>

                    {expandedInstallation === installation._id && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="mb-4">
                          <h4 className="font-medium mb-2">
                            System Information
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {renderEditableField(
                              installation,
                              "pumpNumber",
                              "Pump Number",
                              installation.pumpNumber,
                            )}
                            {renderEditableField(
                              installation,
                              "motorNumber",
                              "Motor Number",
                              installation.motorNumber,
                            )}
                            {renderEditableField(
                              installation,
                              "controllerNumber",
                              "Controller Number",
                              installation.controllerNumber,
                            )}
                            {renderEditableField(
                              installation,
                              "rmuNumber",
                              "RMU Number",
                              installation.rmuNumber,
                            )}
                            {renderItemDetail(
                              "System",
                              installation.systemId?.systemName,
                            )}
                            {renderItemDetail(
                              "Warehouse",
                              installation.warehouseId?.warehouseName,
                            )}
                            {renderItemDetail(
                              "Installation Status",
                              installation.installationDone
                                ? "Completed"
                                : "Pending",
                            )}
                          </div>
                        </div>

                        {renderPanelNumbers(installation)}

                        {installation.extraPanelNumbers?.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium mb-2">
                              Extra Panel Numbers (
                              {installation.extraPanelNumbers.length})
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {installation.extraPanelNumbers.map(
                                (panel, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 bg-gray-100 rounded text-xs"
                                  >
                                    Panel {index + 1}: {panel}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mb-4">
                          <h4 className="font-medium mb-2">
                            Items List ({installation.itemsList?.length || 0})
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {installation.itemsList?.length > 0 ? (
                              installation.itemsList.map((item, index) => (
                                <div
                                  key={index}
                                  className="border border-gray-200 rounded p-2"
                                >
                                  <div className="font-medium text-sm">
                                    {item.systemItemId?.itemName || "Item"}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    <span>Qty: {item.quantity}</span>
                                    {item.systemItemId?.itemCode && (
                                      <span className="ml-2">
                                        Code: {item.systemItemId.itemCode}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-gray-500">
                                No items listed
                              </div>
                            )}
                          </div>
                        </div>

                        {installation.extraItemsList?.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium mb-2">
                              Extra Items List (
                              {installation.extraItemsList.length})
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {installation.extraItemsList.map(
                                (item, index) => (
                                  <div
                                    key={index}
                                    className="border border-gray-200 rounded p-2"
                                  >
                                    <div className="font-medium text-sm">
                                      {item.systemItemId?.itemName ||
                                        (typeof item.systemItemId === "string"
                                          ? "Item ID: " + item.systemItemId
                                          : "Item")}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      <span>Qty: {item.quantity}</span>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                        {/* Photos section - only shown if includePhoto is true and photos exist */}
                        {includePhoto &&
                          installation.photos &&
                          installation.photos.length > 0 && (
                            <div className="mb-4">
                              <h4 className="font-medium mb-2">
                                Installation Photos
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {installation.photos.map((photo, idx) => (
                                  <img
                                    key={idx}
                                    src={photo}
                                    alt={`Installation ${idx + 1}`}
                                    className="w-24 h-24 object-cover rounded border shadow-sm"
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPagesFromApi > 1 && (
                <div className="flex justify-center mt-6">
                  <nav className="flex items-center gap-1">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded border ${
                        currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-600">
                      Page {currentPage} of {totalPagesFromApi}
                    </span>
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPagesFromApi}
                      className={`px-3 py-1 rounded border ${
                        currentPage === totalPagesFromApi
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No installations found for {selectedPerson.name}
              {(searchTerm || filterStatus !== "all") &&
                " matching your filters"}
            </div>
          )
        ) : (
          <div className="text-center text-gray-500 py-8">
            Please select a service person to view their installations
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovedDataMaharastra;